// src/services/enduser/subscriptionService.ts

import { API_BASE_URL } from '../../config/api';




// Helper function to get session token from cookies
const getSessionToken = (): string | null => {
  const cookies = document.cookie.split(';');
  const sessionCookie = cookies.find(cookie =>
    cookie.trim().startsWith('enduser_session_token=')
  );

  if (sessionCookie) {
    return sessionCookie.split('=')[1].trim();
  }

  return null;
};

// Helper function to create FormData (UPDATED with session_token support)
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  // Always add session_token from cookies if available
  const sessionToken = getSessionToken();
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });

  return formData;
};

// Helper function to handle API responses (same as enduserAuthService)
const handleApiResponse = async (response: Response): Promise<any> => {
  // console.log('Response status:', response.status);
  // console.log('Response ok:', response.ok);

  if (!response.ok) {
    console.error('HTTP error! status:', response.status);

    try {
      const errorText = await response.text();
      console.error('Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    } catch (e) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  try {
    const text = await response.text();
    // console.log('Raw response text:', text);

    if (!text) {
      throw new Error('Empty response body');
    }

    const result = JSON.parse(text);
    // console.log('Parsed JSON result:', result);
    return result;

  } catch (error) {
    console.error('Failed to parse JSON:', error);
    throw new Error('Failed to parse response as JSON');
  }
};

// Interfaces
interface SubscriptionPlan {
  id: number;
  plan_name: string;
  name_hebrew: string;
  duration_months: number;
  monthly_price: number;
  total_price: number;
  description: string;
  features: string[];
  popular: boolean;
  discount_percentage: number;
}

interface CreatePaymentData {
  plan_id: number;
  save_card_token: number;
  user_consent: boolean;
}

interface PaymentResponse {
  success: boolean;
  auth_code?: string;
  process_id?: string;
  process_token?: string;
  expires_in?: number;
  error?: string;
  order_id?: number;
}

interface ApiResponse<T = any> {
  res: 'OK' | 'NOK';
  msg: string;
  data?: T;
  error_code?: string;
}

interface UserSubscriptionStatus {
  subscription_status: 'none' | 'trial' | 'active' | 'expired' | 'cancelled' | 'free_access';
  can_cancel_within_48h: boolean;
  trial_ends_at?: string;
  subscription_ends_at?: string;
  can_calculate: boolean;
  subscription_details?: {
    plan_name: string;
    plan_name_hebrew: string;
    amount_paid: number;
    payment_date: string;
  };
}

// ===== ניסיון חינם בעזרת קוד אימות (מאי 2026) =====
interface SendFreeTrialCodeData {
  // טלפון אופציונלי - נשלח רק אם נערך במסך המקדים
  phone_number?: string;
}

interface SendFreeTrialCodeResponse {
  delivery_method: 'sms' | 'email';
  phone_number?: string;
  email?: string;
}

interface VerifyFreeTrialCodeResponse {
  free_trial_activated: boolean;
  free_trial_remaining: number;
}

class SubscriptionService {

  /**
   * Get all active subscription plans
   */
  async getSubscriptionPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    // console.log('=== GET SUBSCRIPTION PLANS START ===');

    try {
      // Check if we have session token
      // const sessionToken = getSessionToken();
      // console.log('🍪 Session token available:', !!sessionToken);

      const formData = createFormData({});

      // Debug FormData contents
      // console.log('📋 FormData contents:');
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        // console.log(`  ${key}: ${key === 'session_token' ? value.toString().substring(0, 8) + '...' : value}`);
      });

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/get_subscription_plans`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_subscription_plans`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);
      // console.log('=== GET SUBSCRIPTION PLANS SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== GET SUBSCRIPTION PLANS ERROR ===');
      console.error('Get plans error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בטעינת תוכניות המנוי - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Create payment process for subscription
   */
  async createPayment(data: CreatePaymentData): Promise<ApiResponse<PaymentResponse>> {
    // console.log('=== CREATE SUBSCRIPTION PAYMENT START ===');
    // console.log('Payment data:', data);

    try {
      // Check if we have session token
      const sessionToken = getSessionToken();
      // console.log('🍪 Session token available:', !!sessionToken);

      if (!sessionToken) {
        return {
          res: 'NOK',
          msg: 'לא נמצא session token - אנא התחבר מחדש',
          error_code: 'NO_SESSION_TOKEN'
        };
      }

      const formData = createFormData(data);

      // Debug FormData contents
      // console.log('📋 FormData contents:');
      const entries2 = Array.from(formData.entries());
      entries2.forEach(([key, value]) => {
        // console.log(`  ${key}: ${key === 'session_token' ? value.toString().substring(0, 8) + '...' : value}`);
      });

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/create_payment`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/create_payment`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);
      // console.log('=== CREATE SUBSCRIPTION PAYMENT SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== CREATE SUBSCRIPTION PAYMENT ERROR ===');
      console.error('Create payment error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה ביצירת תשלום - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Get current user subscription status
   */
  async getSubscriptionStatus(): Promise<ApiResponse<UserSubscriptionStatus>> {
    // console.log('=== GET SUBSCRIPTION STATUS START ===');

    try {
      // const sessionToken = getSessionToken();
      // console.log('🍪 Session token available:', !!sessionToken);

      const formData = createFormData({});

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/get_subscription_status`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_subscription_status`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);
      // console.log('=== GET SUBSCRIPTION STATUS SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== GET SUBSCRIPTION STATUS ERROR ===');
      console.error('Get subscription status error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בבדיקת סטטוס מנוי - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Cancel subscription within 48 hours
   */
  async cancelSubscription(): Promise<ApiResponse> {
    // console.log('=== CANCEL SUBSCRIPTION START ===');

    try {
      // const sessionToken = getSessionToken();
      // console.log('🍪 Session token available:', !!sessionToken);

      const formData = createFormData({});

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/cancel_subscription`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/cancel_subscription`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);
      // console.log('=== CANCEL SUBSCRIPTION SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== CANCEL SUBSCRIPTION ERROR ===');
      console.error('Cancel subscription error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בביטול המנוי - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * ניסיון חינם: שליחת קוד אימות (SMS לישראלי / מייל לחו"ל).
   * הטלפון נשלח רק אם נערך במסך המקדים (אחרת ה-Backend משתמש בטלפון מהפרופיל).
   * Backend: api_service_enduser_send_free_trial_code · Fields: session_token (+ phone_number אופציונלי)
   */
  async sendFreeTrialCode(data: SendFreeTrialCodeData = {}): Promise<ApiResponse<SendFreeTrialCodeResponse>> {
    try {
      const sessionToken = getSessionToken();
      if (!sessionToken) {
        return {
          res: 'NOK',
          msg: 'לא נמצא session token - אנא התחבר מחדש',
          error_code: 'NO_SESSION_TOKEN'
        };
      }

      const formData = createFormData(data);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/send_free_trial_code`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      return await handleApiResponse(response);

    } catch (error) {
      console.error('=== SEND FREE TRIAL CODE ERROR ===');
      console.error('Send free trial code error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בשליחת קוד האימות - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * ניסיון חינם: אימות הקוד והפעלת הניסיון.
   * Backend: api_service_enduser_verify_free_trial_code · Fields: session_token, code
   */
  async verifyFreeTrialCode(code: string): Promise<ApiResponse<VerifyFreeTrialCodeResponse>> {
    try {
      const sessionToken = getSessionToken();
      if (!sessionToken) {
        return {
          res: 'NOK',
          msg: 'לא נמצא session token - אנא התחבר מחדש',
          error_code: 'NO_SESSION_TOKEN'
        };
      }

      const formData = createFormData({ code });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/verify_free_trial_code`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      return await handleApiResponse(response);

    } catch (error) {
      console.error('=== VERIFY FREE TRIAL CODE ERROR ===');
      console.error('Verify free trial code error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה באימות הקוד - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * קופון גישה חופשית: מימוש קוד קופון.
   * Backend: api_service_enduser_redeem_coupon · Fields: session_token, code
   */
  async redeemCoupon(code: string): Promise<ApiResponse<any>> {
    try {
      const sessionToken = getSessionToken();
      if (!sessionToken) {
        return {
          res: 'NOK',
          msg: 'לא נמצא session token - אנא התחבר מחדש',
          error_code: 'NO_SESSION_TOKEN'
        };
      }

      const formData = createFormData({ code });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/redeem_coupon`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      return await handleApiResponse(response);

    } catch (error) {
      console.error('Redeem coupon error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה במימוש הקופון - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Helper functions for UI
   */

  formatPrice(price: number): string {
    return `₪${price.toLocaleString('he-IL')}`;
  }

  calculateSavings(monthlyPrice: number, totalPrice: number, months: number) {
    const regularPrice = monthlyPrice * months;
    const savings = regularPrice - totalPrice;
    const percentage = Math.round((savings / regularPrice) * 100);
    return { savings, percentage };
  }

  formatDuration(months: number): string {
    if (months === 1) return 'חודש';
    if (months === 3) return 'רבעון';
    if (months === 12) return 'שנה';
    return `${months} חודשים`;
  }

  canUserAccessCalculators(subscriptionStatus?: UserSubscriptionStatus): boolean {
    if (!subscriptionStatus) return false;
    return subscriptionStatus.can_calculate;
  }

  shouldShowSubscriptionPage(subscriptionStatus?: UserSubscriptionStatus): boolean {
    if (!subscriptionStatus) return true;
    return subscriptionStatus.subscription_status === 'none' ||
      subscriptionStatus.subscription_status === 'expired';
  }

  canCancelSubscription(subscriptionStatus?: UserSubscriptionStatus): boolean {
    if (!subscriptionStatus) return false;
    return subscriptionStatus.can_cancel_within_48h;
  }

  getSubscriptionStatusText(subscriptionStatus?: UserSubscriptionStatus): string {
    if (!subscriptionStatus) return 'לא נמצא מידע על מנוי';

    switch (subscriptionStatus.subscription_status) {
      case 'trial':
        return `תקופת ניסיון${subscriptionStatus.trial_ends_at ? ' עד ' + new Date(subscriptionStatus.trial_ends_at).toLocaleDateString('he-IL') : ''}`;
      case 'active':
        return `מנוי פעיל${subscriptionStatus.subscription_ends_at ? ' עד ' + new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString('he-IL') : ''}`;
      case 'expired':
        return 'המנוי פג תוקף';
      case 'cancelled':
        return 'המנוי בוטל';
      case 'none':
      default:
        return 'אין מנוי פעיל';
    }
  }

  /**
   * Check if user has valid session token
   */
  hasValidSessionToken(): boolean {
    return !!getSessionToken();
  }

}
export const checkGraceEligibility = async (): Promise<{
  eligible_for_grace_period: boolean;
  message: string;
}> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token');
  }

  const formData = new FormData();
  formData.append('session_token', sessionToken);

  const response = await fetch(`${API_BASE_URL}/service/enduser/check_grace_eligibility`, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (data.res !== 'OK') {
    throw new Error(data.msg || 'Failed to check grace eligibility');
  }
  return data.data;
};
// Export singleton instance
export const subscriptionService = new SubscriptionService();
export default subscriptionService;